import {useVipLocalStore, VipLocalStore} from '@modules'
import {autorun, runInAction, toJS, trace} from 'mobx'
import {CLASSES, I18nLabel, Actions, TabNode, TabSetNode, showPopup, ILayoutCallbacks, TabButton} from '@flexlayout'
import {React, action, classNames, computed, observable, observer, useAsObservableSource, useLayoutEffect, jsondiff} from '@api'
import {IDisposer} from 'mobx-utils'
import {useEffect} from 'react'
import * as css from './TabSet.css'
import {globalStores} from '@stores'
import {IIcons, LayoutStore} from './Layout'

/** @hidden @internal */
export interface ITabSetProps {
  layout: ILayoutCallbacks;
  node: TabSetNode;
  iconFactory?: (node: TabNode) => React.ReactNode | undefined;
  titleFactory?: (node: TabNode) => React.ReactNode | undefined;
  icons?: IIcons;
  editingTab?: TabNode;
}

/** @hidden @internal */

/// region Store

export const TabSetStoreContext = React.createContext<TabSetStore>(null)
export const useTabSetStore = () => React.useContext(TabSetStoreContext)

export class TabSetStore extends VipLocalStore {
  constructor(s, public props: ITabSetProps) {
    super(s)
  }

  get layout() { return (this.props.layout as LayoutStore)}

  get node() { return this.props.node}

  get flexLayout() { return globalStores.flexLayout}

  _toDispose: Array<IDisposer> = []

  dispose = () => {
    this._toDispose.forEach(f => f())
    this.ref.removeEventListener('wheel', this.onMouseWheel)
  }

  registerEffects = () => {
    const {_toDispose} = this

    this.node.component = this

    _toDispose.push(
      autorun(() => {
        const {ref} = this
        ref && ref.addEventListener('wheel', this.onMouseWheel, {passive: false})
      }),

      autorun(() => {
        const {tabbarInner, node: {rect: {width}, selectedNode}} = this
        // trace()
        if (selectedNode?.tabStore?.ref) {
          const {tabStore: {ref}} = selectedNode
          // console.log({ref, name: selectedNode.displayName})
          this._debouncedScrollIntoView()
        }
      }),
    )
  }

  @observable.ref toolbar: HTMLDivElement
  @observable.ref toolbarRef: React.MutableRefObject<HTMLDivElement | null>
  @observable.ref tabbarInner: HTMLDivElement
  @observable.ref tabbarInnerRef: React.MutableRefObject<HTMLDivElement | null>
  @observable.ref stickyButtons: HTMLDivElement
  @observable.ref stickyButtonsRef: React.MutableRefObject<HTMLDivElement | null>
  @observable.ref userControlledLeft: React.MutableRefObject<boolean>
  @observable hiddenTabs = observable.array<{ node: TabNode, index: number }>([], {deep: false})
  @observable tabsTruncated = false

  @computed get showHeader() { return false && this.node.name !== undefined }

  @computed get selectedTabNode(): TabNode { return this.node.selectedNode as TabNode}

  @action onOverflowItemSelect = (item: { node: TabNode; index: number }) => {
    this.layout.doAction(Actions.selectTab(item.node.id))
    this.userControlledLeft.current = false
  }

  @action onMouseDown = (event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    if ((event as React.MouseEvent).button === 2) return

    const elem = event.target as HTMLElement
    if (!event.currentTarget.contains(elem)) {
      return
    }

    if (elem.closest(`.${CLASSES.FLEXLAYOUT__BORDER_BUTTON}, .${CLASSES.FLEXLAYOUT__TAB_BUTTON}`)) {
      return
    }

    const {node, layout, onDoubleClick} = this
    let name = node.name
    if (name === undefined) {
      name = ''
    } else {
      name = ': ' + name
    }
    layout.doAction(Actions.setActiveTabset(node.id))
    if (!layout.editingTab) {
      const message = layout.i18nName(I18nLabel.Move_Tabset, name)
      layout.dragStart(event, message, node, node.isEnableDrag, (event2: Event) => undefined, onDoubleClick)
    }
  }

  @action onInterceptMouseDown = (event: React.MouseEvent | React.TouchEvent) => {
    if ((event as React.MouseEvent).button === 2) return

    event.stopPropagation()
  }

  @action onMaximizeToggle = () => {
    const {node, layout} = this
    if (node.canMaximize) {
      layout.maximize(node)
    }
  }

  @action onFloatTab = () => {
    const {node, layout, selectedTabNode} = this
    if (selectedTabNode !== undefined) {
      layout.doAction(Actions.floatTab(selectedTabNode.id))
    }
  }

  @action onDoubleClick = (event: Event) => {
    const {node, layout} = this
    // if (node.canMaximize) {
    //   layout.maximize(node)
    // }
  }

  @computed get tabStripStyle() {
    const {node, showHeader} = this
    const tabStripStyle: { [key: string]: string } = {height: node.tabStripHeight + 'px'}
    if (node.tabLocation === 'top') {
      const top = showHeader ? node.headerHeight + 'px' : '0px'
      tabStripStyle['top'] = top
    } else {
      tabStripStyle['bottom'] = '0px'
    }
    return tabStripStyle
  }

  @computed get hideTabStrip() {
    const {flexLayout: {borderForKnownElement}, node: {children, enableTabStrip}} = this
    if (!enableTabStrip) return true

    return false

    // Do this if we can keep the border nodes always showing on the sidebar even when dragged into main gui

    // return children.every(tab => borderForKnownElement[tab.id] != null)
  }

  @computed get tabStripClasses() {
    const {layout: {getClassName: cm}, node, showHeader} = this
    let tabStripClasses = cm(CLASSES.FLEXLAYOUT__TABSET_TABBAR_OUTER)
    if (node.classNameTabStrip !== undefined) {
      tabStripClasses += ' ' + node.classNameTabStrip
    }
    tabStripClasses += ' ' + CLASSES.FLEXLAYOUT__TABSET_TABBAR_OUTER_ + node.tabLocation

    if (node.isActive && !showHeader) {
      tabStripClasses += ' ' + cm(CLASSES.FLEXLAYOUT__TABSET_SELECTED)
    }

    if (node.isMaximized && !showHeader) {
      tabStripClasses += ' ' + cm(CLASSES.FLEXLAYOUT__TABSET_MAXIMIZED)
    }

    return tabStripClasses
  }

  @observable position: number

  get cm() { return this.layout.getClassName}

  @computed get tabs() {
    const {node, props: {iconFactory, titleFactory, icons}, layout, tabsTruncated, renderState: {stickyButtons}, stickyButtonsRef, onInterceptMouseDown, cm} = this

    // trace()
    let tabs = []
    // Always show them
    if (node.enableTabStrip) {
      let children = node.children

      // Breaks things elsewhere
      //children = children.filter((tab: TabNode) => tab.component != 'catalog' || !selectedDeploymentId || tab.config.deployment === selectedDeploymentId)

      children = _.orderBy(children, (c: TabNode) => c.attributes.config?.pin ? 0 : 1)

      for (let i = 0; i < children.length; i++) {
        const child = children[i] as TabNode
        let isSelected = node.selected === i
        tabs.push(
          <TabButton
            layout={layout}
            node={child}
            key={child.id}
            selected={isSelected}
            show={true}
            height={node.tabStripHeight}
            iconFactory={iconFactory}
            titleFactory={titleFactory}
            icons={icons}
          />
        )
      }
    }
    tabs = _.orderBy(tabs)

    if (stickyButtons.length > 0) {
      if (!tabsTruncated) {
        tabs.push(<div
          ref={stickyButtonsRef}
          key='sticky_buttons_container'
          onMouseDown={onInterceptMouseDown}
          onTouchStart={onInterceptMouseDown}
          onDragStart={(e) => { e.preventDefault() }}
          className={cm(CLASSES.FLEXLAYOUT__TAB_TOOLBAR_STICKY_BUTTONS_CONTAINER)}
        >
          {stickyButtons}
        </div>)
      }
    }

    return tabs
  }

  @computed get renderState() {
    const {node, layout} = this

    // allow customization of header contents and buttons
    const renderState = {headerContent: node.name, stickyButtons: [], buttons: [], headerButtons: []}
    layout.customizeTabSet(node, renderState)

    let {buttons, stickyButtons, headerButtons, headerContent} = renderState,
      {
        tabsTruncated, hiddenTabs, onMaximizeToggle, showHeader, layout: {getClassName: cm}, onInterceptMouseDown, onFloatTab, selectedTabNode,
        props: {icons}
      } = this

    if (stickyButtons.length > 0) {
      if (tabsTruncated) {
        buttons = [...stickyButtons, ...buttons]
      }
    }

    if (hiddenTabs.length > 0) {
      buttons.push(<OverflowButton key='overflowButton'/>)
    }

    if (selectedTabNode !== undefined && layout.supportsPopout && selectedTabNode.isEnableFloat && !selectedTabNode.isFloating) {
      const floatTitle = layout.i18nName(I18nLabel.Float_Tab)
      buttons.push(
        <button
          key='float'
          title={floatTitle}
          className={cm(CLASSES.FLEXLAYOUT__TAB_TOOLBAR_BUTTON) + ' ' + cm(CLASSES.FLEXLAYOUT__TAB_TOOLBAR_BUTTON_FLOAT)}
          onClick={onFloatTab}
          onMouseDown={onInterceptMouseDown}
          onTouchStart={onInterceptMouseDown}
        >
          {icons?.popout}
        </button>
      )
    }

    const btns = showHeader ? headerButtons : buttons
    if (!layout?.props?.onRenderTabSet && node.canMaximize) {
      const minTitle = layout.i18nName(I18nLabel.Restore)
      const maxTitle = layout.i18nName(I18nLabel.Maximize)
      btns.push(
        <button
          key='max'
          title={node.isMaximized_OldFlexLayout ? minTitle : maxTitle}
          className={cm(CLASSES.FLEXLAYOUT__TAB_TOOLBAR_BUTTON) + ' ' + cm(CLASSES.FLEXLAYOUT__TAB_TOOLBAR_BUTTON_ + (node.isMaximized_OldFlexLayout ? 'max' : 'min'))}
          onClick={onMaximizeToggle}
          onMouseDown={onInterceptMouseDown}
          onTouchStart={onInterceptMouseDown}
        >
          {node.isMaximized ? icons?.restore : icons?.maximize}
        </button>
      )
    }

    return {
      buttons, stickyButtons, headerButtons, headerContent
    }
  }

  @observable ref: HTMLDivElement
  setRef = r => this.ref = r

  onMouseWheel = (e: Event) => {
    e.preventDefault()
  }

  @computed.struct get style(): any {
    const {node} = this
    return node._styleWithPosition()
  }

  private _debouncedScrollIntoView = _.debounce(() => {
    const {node: {tabLocation, selectedNode}, tabbarInner: scrollContainer} = this

    if (tabLocation === 'top' && selectedNode?.ref && scrollContainer) {
        //this.ref.scrollIntoView({behavior: 'auto', block: 'nearest', inline: 'end'})
        const {node: {lastTab}} = this, {ref, indexInParent, isLastItemInTabGroup} = selectedNode
        if (scrollContainer.clientWidth < lastTab.ref.offsetLeft + lastTab.ref.offsetWidth) {
          scrollContainer.scrollTo({
            behavior: 'smooth',
            left: indexInParent === 0 ? 0 : Math.max(ref.offsetLeft - 100, 0)
                                      // : isLastItemInTabGroup ?
                                      //                        : ref.offsetLeft // + ref.offsetWidth + 10
          })
        } else {
          scrollContainer.scrollTo({left: 0})
        }
    }
  }, 50)
}

export const TabSet = observer<ITabSetProps>(function TabSet(props) {
  const obsProps = useAsObservableSource(props),
    store = useVipLocalStore((s) => new TabSetStore(s, obsProps)),
    {flexLayout, layout: {getClassName: cm}, node, setRef, style} = store

  const stickyButtonsRef = React.useRef<HTMLDivElement | null>(null)
  useEffect(() => store.registerEffects(), [])
  // needed to prevent default mouse wheel over tabset/border (cannot do with react event?)

  /*
  const {selfRef, onMouseWheel, tabsTruncated, position, hiddenTabs, userControlledLeft} = useTabOverflow(node, Orientation.HORZ, toolbarRef, stickyButtonsRef)

  false && useLayoutEffect(() => {
    runInAction(() => {
      // console.log(toJS({before: _.pick(store, ['position', 'hiddenTabs', 'userControlledLeft', 'tabsTruncated']), after: {position, hiddenTabs, userControlledLeft, tabsTruncated}}, {recurseEverything: true}))
      store.position = position
      if (store.hiddenTabs.length != hiddenTabs.length) {
        store.hiddenTabs.replace(hiddenTabs)
      }
      store.userControlledLeft = userControlledLeft
      store.tabsTruncated = tabsTruncated
    })
  }, [position, userControlledLeft, hiddenTabs, tabsTruncated])
   */

  useLayoutEffect(() => {store.stickyButtons = stickyButtonsRef.current}, [stickyButtonsRef.current])

  //style = layout.styleFont(style)

  // console.log(`Rendering tabset ${node.id}`)
  return (
    <TabSetStoreContext.Provider value={store}>
      <div style={style}
           tabIndex={0}
           data-id={node.id}
           data-tabset-index={node.indexInParent}
           data-hide={node.parent.hidden}
           //data-show-collapsed={node.showCollapsed} // causes layout thrashing
           data-tabs={node.tabs.length}
           data-type='TabSet'
           className={classNames(css.root, cm(CLASSES.FLEXLAYOUT__TABSET))}
           onContextMenu={flexLayout.onContextMenu_forTabSetNode(node)}
           ref={setRef}>
        <TabSetHeader/>
        <TabSetTabStrip/>
      </div>
    </TabSetStoreContext.Provider>
  )
})

const TabSetHeader = observer(function TabSetHeader() {
  const store = useTabSetStore(),
    {toolbarRef, showHeader, onInterceptMouseDown, layout: {getClassName: cm}, node, onMouseDown, renderState: {headerContent, headerButtons}} = store

  if (showHeader) {
    const headerToolbar = (
      <div key='toolbar' ref={toolbarRef}
           className={cm(CLASSES.FLEXLAYOUT__TAB_TOOLBAR)}
           onMouseDown={onInterceptMouseDown}
           onTouchStart={onInterceptMouseDown}
           onDragStart={(e) => { e.preventDefault() }}
      >
        {headerButtons}
      </div>
    )

    let tabHeaderClasses = classNames(cm(CLASSES.FLEXLAYOUT__TABSET_HEADER), node.isActive && cm(CLASSES.FLEXLAYOUT__TABSET_SELECTED),
      node.isMaximized_OldFlexLayout && cm(CLASSES.FLEXLAYOUT__TABSET_MAXIMIZED),
      node.classNameHeader
    )

    return (
      <div className={classNames(css.tabHeader, tabHeaderClasses)}
           style={{height: `${Math.max(0, node.headerHeight ?? 0)}px`}}
           onMouseDown={onMouseDown}
           onTouchStart={onMouseDown}>
        <div className={cm(CLASSES.FLEXLAYOUT__TABSET_HEADER_CONTENT)}>{headerContent}</div>
        {headerToolbar}
      </div>
    )
  }

  return null
})

const TabSetTabStrip = observer(function TabSetTabStrip() {
  const store = useTabSetStore(),
    {tabStripClasses, hideTabStrip, layout: {getClassName: cm}, onMouseDown, tabStripStyle, node, renderState: {buttons}, position, tabs, onInterceptMouseDown} = store

  const tabbarInnerRef = React.useRef<HTMLDivElement | null>(null),
    toolbarRef = React.useRef<HTMLDivElement | null>(null)

  useLayoutEffect(() => {
    runInAction(() => {
      store.tabbarInnerRef = tabbarInnerRef
      store.tabbarInner = tabbarInnerRef.current
      store.toolbarRef = toolbarRef
      store.toolbar = toolbarRef.current
    })
  }, [tabbarInnerRef, tabbarInnerRef.current, toolbarRef, toolbarRef.current])


  return (
    <div className={classNames(tabStripClasses, css.tabStrip)}
         style={tabStripStyle}
         data-hide={hideTabStrip}
         data-active={node.isActive}
         tabIndex={0}
         onMouseDown={onMouseDown}
         data-collapsed={!node.enableTabStrip}
         onTouchStart={onMouseDown}
    >
      <div ref={tabbarInnerRef} className={cm(CLASSES.FLEXLAYOUT__TABSET_TABBAR_INNER) + ' ' + cm(CLASSES.FLEXLAYOUT__TABSET_TABBAR_INNER_ + node.tabLocation)}>
        <div
          //disableAllAnimations={node.flexLayout.showLayoutPreview}

          style={{left: position ?? 0}}
          className={cm(CLASSES.FLEXLAYOUT__TABSET_TABBAR_INNER_TAB_CONTAINER) + ' ' + cm(CLASSES.FLEXLAYOUT__TABSET_TABBAR_INNER_TAB_CONTAINER_ + node.tabLocation)}
        >
          {tabs}
        </div>
      </div>
      {!_.isEmpty(buttons) && <div key='toolbar' ref={toolbarRef}
           className={cm(CLASSES.FLEXLAYOUT__TAB_TOOLBAR)}
           onMouseDown={onInterceptMouseDown}
           onTouchStart={onInterceptMouseDown}
           onDragStart={(e) => { e.preventDefault() }}
      >
        {buttons}
      </div>}
    </div>
  )
})

const OverflowButton = observer(function OverflowButton() {
  const store = useTabSetStore(),
    {onInterceptMouseDown, onOverflowItemSelect, layout, layout: {getClassName: cm}, props: {icons}, hiddenTabs} = store,
    overflowTitle = layout.i18nName(I18nLabel.Overflow_Menu_Tooltip),
    overflowbuttonRef = React.useRef<HTMLButtonElement | null>(null)

  // Todo - replace this with a popover2 and a primary icon button
  return (<button
    ref={overflowbuttonRef}
    className={cm(CLASSES.FLEXLAYOUT__TAB_BUTTON_OVERFLOW)}
    title={overflowTitle}
    onClick={() => showPopup(layout.rootDiv, overflowbuttonRef.current, hiddenTabs, onOverflowItemSelect, layout.getClassName)}
    onMouseDown={onInterceptMouseDown}
    onTouchStart={onInterceptMouseDown}
  >
    {icons?.more}
    {hiddenTabs.length}
  </button>)
})


// WEBPACK FOOTER //
// ./src/stores/catalog/flexLayout/flexlayout-react/src/view/TabSet.tsx